import DateRangePicker from "@wojtekmaj/react-daterange-picker/dist/DateRangePicker";
import classnames from "classnames";
import { CalendarMonth, TeamMember, Teams } from "components/assets/Icons";
import Button, { SIZE } from "components/Button";
import UserInfo from "components/UserInfo";
import { useEffect, useRef, useState } from "react";
import { MultiSelect } from "react-multi-select-component";
import { padTo2Digits } from "utils/modifyDate";
import { useDispatch, useSelector } from "react-redux";
import { membersList, membersListOddo } from "actions/members";

import styles from "./ReportForm.module.scss";

const ReportForm = ({
  isLoggedIn,
  originUrl,
  bypassToken,
  handleOnSubmit,
  showButton,
  params,
  formError,
  employeesPhotos,
  setEmployeesPhotos,
  withTime = true,
  timezone,
  setTimezone,
  timezoneOptions,
}) => {
  const ref = useRef();
  const dispatch = useDispatch();
  const [value, onChange] = useState([new Date(), new Date()]);
  const [selected, setSelected] = useState([]);
  const [open, setOpen] = useState(true);
  const [teams, setTeams] = useState([]);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [membersData, setMembersData] = useState([]);
  const data = useSelector((state) => state.members);

  useEffect(() => {
    if(isLoggedIn) {
      dispatch(membersList())
    } else {
      if(originUrl) {
        dispatch(membersListOddo(undefined, undefined, originUrl, bypassToken))
      }
    };
  }, [dispatch, originUrl]);

  useEffect(() => {
    data?.list && setMembersData(data?.list);
  }, [data]);

  const groupBy = (array, key) => {
    return array.reduce((result, currentValue) => {
      (result[currentValue[key]] = result[currentValue[key]] || []).push(
        currentValue
      );
      return result;
    }, {});
  };

  useEffect(() => {
    setTeams(groupBy(membersData, "team_id__name"));
  }, [membersData]);

  useEffect(() => {
    membersData &&
      membersData?.map(({ id, photo_url }) => {
        return setEmployeesPhotos(employeesPhotos.set(id, photo_url));
      });
  }, [membersData, employeesPhotos, setEmployeesPhotos]);

  params.dateFrom = `${value[0].getFullYear()}-${padTo2Digits(
    value[0].getMonth() + 1
  )}-${padTo2Digits(value[0].getDate())}${withTime ? "T00:00" : ""}`;

  params.dateTo = `${value[1].getFullYear()}-${padTo2Digits(
    value[1].getMonth() + 1
  )}-${padTo2Digits(value[1].getDate())}${withTime ? "T00:00" : ""}`;

  params.employeesList = selected
    ?.map(({ value }) => value)
    .filter((value) => value !== null)
    .join();

  /* For Team Members */
  const teamMembersOptions =
    membersData &&
    membersData?.map(({ first_name, last_name, photo_url, id }) => {
      let name = `${first_name && first_name} ${last_name ? last_name : ""}`
      return {
        label: <UserInfo src={photo_url} name={name} />,
        value: id,
      };
    });

  const teamMembersValueRenderer = (selected) => {
    if (!selected.length) {
      return <TeamMember />;
    }

    if (selected.length > 1) {
      return (
        <>
          <TeamMember /> {`${selected.length} selected`}
        </>
      );
    } else if (selected.length === 1) {
      const element = selected[0]?.label.props;
      return (
        <UserInfo src={element.src} name={element.name && element.name} className="photo" />
      );
    }
  };

  const teamMembersFilterOptions = (options, filter) => {
    if (!filter) {
      return options;
    }
    return options.filter(({ label }) => {
      return label && label?.props?.name.toLowerCase().includes(filter);
    });
  };

  /* For Teams */
  const teamsOptions = Object.keys(teams)?.map((item) => {
    return {
      label: item !== "null" ? item : "No Team Assigned",
      value: teams[item],
    };
  });

  const teamsValueRenderer = (selected) => {
    if (!selected.length) {
      return <Teams />;
    }

    if (selected.length > 1) {
      return (
        <>
          <Teams /> {`${selected.length} selected`}
        </>
      );
    } else if (selected.length === 1) {
      return (
        <>
          <Teams /> {selected[0]?.label}
        </>
      );
    }
  };

  const teamsFilterOptions = (options, filter) => {
    if (!filter) {
      return options;
    }

    return options.filter(({ label }) => {
      return label && label?.toLowerCase().includes(filter);
    });
  };

  const handleClick = (e) => {
    setSelectedTeams(e);

    const array = [];

    e?.map(({ value }) => {
      const newArray = value?.map(
        ({ photo_url, first_name, last_name, id }) => {
          return {
            label: {
              props: {
                src: photo_url,
                name: first_name + " " + last_name,
              },
            },
            value: id,
          };
        }
      );

      return array.push(...newArray);
    });

    setSelected(array);
  };

  function setHeaderHeight() {
    if (typeof window === "object") {
      return document.documentElement.style.setProperty(
        "--form-height",
        `${ref.current?.offsetHeight}px`
      );
    }
  }

  setHeaderHeight();

  useEffect(() => {
    showButton && setOpen(false);
  }, [showButton]);

  useEffect(() => {
    setHeaderHeight();
  }, [open]);

  return (
    <div ref={ref}>
      {open && (
        <form
          className={styles.wrapper}
          method="GET"
          action="submit"
          onSubmit={handleOnSubmit}
        >
          <span className={styles.group}>
            <label htmlFor="daterange" className={styles.label}>
              From/To
            </label>
            <DateRangePicker
              onChange={onChange}
              value={value}
              format="MM.dd.y"
              clearIcon={null}
              locale="en"
              navigationLabel={({ date }) =>
                date.toLocaleDateString("default", { month: "long" })
              }
              calendarIcon={<CalendarMonth />}
              closeCalendar={false}
              maxDate={new Date()}
            />
          </span>

          {timezone && setTimezone && timezoneOptions && (
            <span className={styles.group}>
              <label htmlFor="timezone" className={styles.label}>
                Timezone
              </label>
              <select
                id="timezone"
                className={styles.timezoneSelect}
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              >
                {timezoneOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </span>
          )}

          <span className={styles.group}>
            <label htmlFor="team" className={styles.label}>
              Team
            </label>
            <MultiSelect
              className={classnames(styles.select, styles.select_middle)}
              options={teamsOptions}
              value={selectedTeams}
              onChange={handleClick}
              valueRenderer={teamsValueRenderer}
              filterOptions={teamsFilterOptions}
            />
          </span>

          <span className={styles.group}>
            <label htmlFor="teamMembers" className={styles.label}>
              Team Members
            </label>
            <MultiSelect
              className={classnames(
                styles.select,
                formError && selected.length === 0 ? styles.error : ""
              )}
              options={teamMembersOptions}
              value={selected}
              onChange={setSelected}
              valueRenderer={teamMembersValueRenderer}
              filterOptions={teamMembersFilterOptions}
            />
            {formError && selected.length === 0 && (
              <p className={styles.error_message}>{formError}</p>
            )}
          </span>

          <Button type="submit" size={SIZE.MIDDLE} className={styles.button}>
            Get Report
          </Button>
        </form>
      )}
      {showButton && (
        <button
          className={classnames(styles.toogle, open ? styles.active : "")}
          onClick={() => setOpen(!open)}
        >
          {open ? "Hide Filters" : "Show Filters"}
        </button>
      )}
    </div>
  );
};

export default ReportForm;
